import { createContext, useContext, useCallback, useMemo, useEffect, type ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { translations, type Lang, type TranslationKey } from './translations'

interface LanguageContextValue {
  lang: Lang
  setLang: (lang: Lang) => void
  t: (key: TranslationKey) => string
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

export const STORAGE_KEY = 'ea-lang'

/**
 * Phase 7: language is now a real URL segment (`/bs/...` / `/en/...`,
 * see App.tsx's `LangRoot`) instead of a client-side-only toggle — needed
 * for real hreflang tags to be possible at all (a hreflang alternate has
 * to point at an actually-different URL). `lang` is passed in as a prop
 * from the matched route param rather than read from localStorage.
 * localStorage is still written (not read) on every language change, purely
 * so the `/` → `/bs` or `/en` root redirect (see `RootRedirect` in App.tsx)
 * can remember a returning visitor's last language.
 */
export function LanguageProvider({ lang, children }: { lang: Lang; children: ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    document.documentElement.lang = lang
    window.localStorage.setItem(STORAGE_KEY, lang)
  }, [lang])

  const setLang = useCallback(
    (next: Lang) => {
      if (next === lang) return
      // Swap only the leading /bs or /en segment, keep the rest of the path + hash + query.
      const rest = location.pathname.replace(/^\/(bs|en)/, '')
      navigate(`/${next}${rest}${location.search}${location.hash}`)
    },
    [lang, location, navigate],
  )

  const t = useCallback((key: TranslationKey) => translations[lang][key] ?? translations.bs[key], [lang])

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider')
  return ctx
}
